import { UseFormReturn } from 'react-hook-form';

import { FieldWithLabel, GenderToggle, Input, NumberInput } from '@app/shared/ui/atoms/Common';
import { useAdminMatrixChannelFormTranslations } from '@app/shared/hooks/admin/useAdminMatrixChannelFormTranslations';
import { capitalize } from '@app/shared/utils/common';
import { FormSchema } from '@app/shared/types/admin/pages/matrixChannelForm';
import { Gender } from '@app/shared/types/common';

interface FormProps {
  form: UseFormReturn<FormSchema, unknown, FormSchema>;
  onSubmit: (values: FormSchema) => void;
  gender: Gender;
  onGenderChange: (value: Gender) => void;
}

export function Form({ form, onSubmit, gender, onGenderChange }: FormProps) {
  const {
    handleSubmit,
    register,
    formState: { errors },
  } = form;
  const { getTranslation, requiredError, rangeError } = useAdminMatrixChannelFormTranslations();

  const cellIdLabel = capitalize(getTranslation('cell_id_with_range'));
  const cellIdRequiredErrorMessage = requiredError('cell_id');
  const cellIdRangeErrorMessage = rangeError(1, 15);
  const arcanaIdLabel = capitalize(getTranslation('arcana_id_with_range'));
  const arcanaIdRequiredErrorMessage = requiredError('arcana_id');
  const arcanaIdRangeErrorMessage = rangeError(1, 22);
  const arcanaNameLabel = capitalize(getTranslation('arcana_name'));
  const arcanaNameRequiredErrorMessage = requiredError('arcana_name');
  const arcanaDescriptionLabel = capitalize(getTranslation('arcana_description'));
  const arcanaDescriptionRequiredErrorMessage = requiredError('arcana_description');

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <FieldWithLabel label={cellIdLabel} hasErrorField error={errors.cellId?.message}>
        <NumberInput
          {...register('cellId', {
            required: { value: true, message: cellIdRequiredErrorMessage },
            min: { value: 1, message: cellIdRangeErrorMessage },
            max: { value: 15, message: cellIdRangeErrorMessage },
          })}
          placeholder={cellIdLabel}
        />
      </FieldWithLabel>
      <FieldWithLabel label={arcanaIdLabel} hasErrorField error={errors.arcanaId?.message}>
        <NumberInput
          {...register('arcanaId', {
            required: { value: true, message: arcanaIdRequiredErrorMessage },
            min: { value: 1, message: arcanaIdRangeErrorMessage },
            max: { value: 22, message: arcanaIdRangeErrorMessage },
          })}
          placeholder={arcanaIdLabel}
        />
      </FieldWithLabel>
      <FieldWithLabel label={arcanaNameLabel} hasErrorField error={errors.arcanaName?.message}>
        <Input
          {...register('arcanaName', {
            required: { value: true, message: arcanaNameRequiredErrorMessage },
          })}
          placeholder={arcanaNameLabel}
        />
      </FieldWithLabel>
      <FieldWithLabel
        label={arcanaDescriptionLabel}
        hasErrorField
        error={errors.arcanaDescription?.message}
      >
        <Input
          {...register('arcanaDescription', {
            required: { value: true, message: arcanaDescriptionRequiredErrorMessage },
          })}
          placeholder={arcanaDescriptionLabel}
        />
      </FieldWithLabel>
      <GenderToggle value={gender} onChange={onGenderChange} />
    </form>
  );
}
